import { Link } from 'react-router';
import { useWallet } from '@solana/wallet-adapter-react';
import { useGameStore } from '../../stores/gameStore';
import { useGameEngine } from '../../hooks/useGameEngine';
import { useCreditsStore } from '../../stores/creditsStore';
import { useSeasonStore } from '../../stores/seasonStore';

export default function GameOverlay() {
  const { phase, score, survivalTimeMs, precisionError, scoreRank, bestRank, endReason, countdownSeconds, sessionId } = useGameStore();
  const { reset } = useGameEngine();
  const { publicKey } = useWallet();
  const fetchCredits = useCreditsStore((state) => state.fetchCredits);
  const phaseName = useSeasonStore((state) => state.phaseName);

  const handlePlayAgain = () => {
    reset();
    // Refetch credits to sync with backend after game consumed a credit
    if (publicKey) {
      fetchCredits(publicKey.toBase58());
    }
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const milliseconds = ms % 1000;
    return `${seconds}.${milliseconds.toString().padStart(3, '0')}s`;
  };

  const formatPoints = (points: number) => points.toLocaleString();

  const formatPrecisionPenalty = (error: number) => {
    const penalty = Math.floor(error * 0.8);
    return penalty > 0 ? `-${penalty}` : '0';
  };

  // Countdown overlay
  if (phase === 'countdown' && countdownSeconds !== null) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-xl overflow-hidden z-30">
        {/* Radial pulse effect */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="w-[300px] h-[300px] rounded-full border border-white/10 animate-ping"
            style={{ animationDuration: '1s' }}
          />
        </div>

        {/* Countdown number/text */}
        <div key={countdownSeconds} className="relative animate-countdown-slam">
          {countdownSeconds > 0 ? (
            <div className="font-display text-[120px] sm:text-[150px] font-bold leading-none text-white"
                 style={{ textShadow: '0 0 60px rgba(255,255,255,0.5)' }}>
              {countdownSeconds}
            </div>
          ) : (
            <div className="font-display text-6xl sm:text-8xl font-bold text-[#00ff88] tracking-[0.1em]"
                 style={{ textShadow: '0 0 60px rgba(0,255,136,0.5)' }}>
              GO!
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="absolute bottom-8 left-0 right-0 text-center">
          <p className="text-white/40 text-xs sm:text-sm tracking-[0.2em] uppercase">
            Keep cursor inside the circle
          </p>
        </div>
      </div>
    );
  }

  // Game over overlay
  if (phase === 'ended') {
    const isSuccess = endReason === 'completed';

    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black/90 rounded-xl overflow-hidden z-30">
        {/* Background effect */}
        <div className="absolute inset-0 flex items-center justify-center">
          {isSuccess ? (
            // Success burst
            <div className="w-[400px] h-[400px] rounded-full bg-[#00ff88]/5 animate-pulse" />
          ) : (
            // Failure effect
            <div className="w-[400px] h-[400px] rounded-full bg-[#ef4444]/5" />
          )}
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 w-full max-w-md">
          {/* Result title */}
          <div className="mb-6 sm:mb-8 animate-scale-in">
            <div
              className={`inline-block px-6 sm:px-8 py-2 sm:py-3 rounded ${
                isSuccess
                  ? 'bg-[#00ff88]/10 border border-[#00ff88]/30'
                  : 'bg-[#ef4444]/10 border border-[#ef4444]/30'
              }`}
            >
              <h2 className={`font-display text-3xl sm:text-5xl font-bold tracking-[0.05em] ${
                isSuccess ? 'text-[#00ff88]' : 'text-[#ef4444]'
              }`}>
                {isSuccess ? 'COMPLETED!' : 'ELIMINATED'}
              </h2>
            </div>
          </div>

          {/* Score */}
          {score !== null && (
            <div className="mb-6 sm:mb-8 animate-slide-up-fade" style={{ animationDelay: '0.1s' }}>
              <div className="text-white/40 text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-2">
                Your Score
              </div>
              <div className="font-mono text-5xl sm:text-7xl font-bold text-white">
                {formatPoints(score)} <span className="text-2xl sm:text-3xl text-white/50">pts</span>
              </div>
              {survivalTimeMs !== null && precisionError !== null && (
                <div className="font-mono text-sm sm:text-base text-white/40 mt-2">
                  {formatTime(survivalTimeMs)} <span className="text-[#ef4444]/60">{formatPrecisionPenalty(precisionError)}</span>
                </div>
              )}
            </div>
          )}

          {/* Ranks */}
          {(scoreRank !== null || bestRank !== null) && (
            <div className="mb-6 sm:mb-8 animate-slide-up-fade" style={{ animationDelay: '0.15s' }}>
              {/* Score Rank - only show if different from best rank */}
              {scoreRank !== null && (
                <div className="mb-3">
                  <div className="text-white/40 text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-1">
                    This Score
                  </div>
                  <span className="font-display text-xl sm:text-2xl font-bold text-white/60">
                    #{scoreRank}
                  </span>
                </div>
              )}

              {/* Best Rank */}
              {bestRank !== null && (
                <div>
                  <div className="text-white/40 text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-1">
                    Your Best Rank
                  </div>
                  <div className="inline-flex items-center gap-2">
                    <span className={`font-display text-2xl sm:text-3xl font-bold ${
                      bestRank <= 256 ? 'text-[#00ff88]' : 'text-white/60'
                    }`}>
                      #{bestRank}
                    </span>
                    {bestRank <= 256 && (
                      <span className="text-[10px] sm:text-xs text-[#00ff88]/60 uppercase tracking-wider">
                        Qualified
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Qualification note */}
              {bestRank !== null && bestRank <= 256 && (
                <p className="mt-3 text-[10px] sm:text-xs text-white/30 max-w-[280px] mx-auto">
                  Rankings may change as others play. Score higher to secure your spot.
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center animate-slide-up-fade" style={{ animationDelay: '0.2s' }}>
            <button
              onClick={handlePlayAgain}
              className="bg-white text-black px-6 sm:px-8 py-3 sm:py-4 font-bold text-sm tracking-[0.1em] uppercase hover:bg-white/90 transition-all"
            >
              Play Again
            </button>
            <Link
              to="/leaderboard"
              className="border border-white/20 text-white/80 px-6 sm:px-8 py-3 sm:py-4 font-semibold text-sm tracking-[0.1em] uppercase hover:border-white/40 hover:text-white transition-all"
            >
              View Leaderboard
            </Link>
          </div>

          {/* Share button */}
          {score !== null && (
            <button
              onClick={() => {
                const isPerfect = survivalTimeMs !== null && survivalTimeMs >= 45000;
                const replayUrl = sessionId ? `https://beatgames.fun/replay/${sessionId}` : 'https://beatgames.fun';
                const scoreText = `${formatPoints(score)} pts`;
                const timeText = survivalTimeMs !== null ? formatTime(survivalTimeMs) : '';
                const text = isPerfect
                  ? `🏆 PERFECT RUN on Hold Steady!\n\nSeason 1 ${phaseName}\n\n${scoreText} (${timeText}). Think you can match it?\n\n${replayUrl}`
                  : `🎯 ${scoreText} on Hold Steady!\n\nSeason 1 ${phaseName}\n\nHow long can you keep your cursor in the shrinking circle?\n\n${replayUrl}`;
                window.open(
                  `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
                  '_blank'
                );
              }}
              className="mt-4 sm:mt-6 inline-flex items-center gap-2 text-white/40 hover:text-white text-xs sm:text-sm transition-colors animate-slide-up-fade"
              style={{ animationDelay: '0.25s' }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              Share on X
            </button>
          )}
        </div>
      </div>
    );
  }

  return null;
}
